import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CALL_BOOST_GAIN_MAX, CALL_BOOST_GAIN_MIN } from '../lib/callAudioBoostPrefs'

/**
 * Popover width in CSS px. Mobile trigger needs this to clamp horizontal
 * position to viewport. Keep in sync with the `w-56` (224px) Tailwind
 * class on the popover container below.
 */
export const CALL_BOOST_POPOVER_WIDTH_PX = 224

// Popover content for the live-call audio boost control. Used by
// CallInfo (desktop) and the mobile active-call row in PhoneCallView.
//
// Position is computed by the trigger and passed in via `position`.
// The trigger is responsible for placing the popover correctly for its
// platform (desktop drops it BELOW the button; mobile rises it ABOVE
// the bottom button row — see PhoneCallView). We just render at fixed
// coordinates relative to the viewport.

export interface CallBoostControlProps {
  enabled: boolean
  gain: number
  onEnabledChange: (v: boolean) => void
  onGainChange: (v: number) => void
  /** Viewport-coordinates for `position: fixed`. */
  position: { top?: number; bottom?: number; left?: number; right?: number }
  onClose: () => void
  /**
   * Ref to the button that toggles this popover. Outside-click handler
   * ignores clicks on this element so the trigger button can toggle
   * itself without race (close-then-reopen).
   */
  triggerRef?: React.RefObject<HTMLElement | null>
  /** Stable id used by tests to find the slider/switch within this dialog. */
  controlsTestId?: string
}

function formatPercent(g: number): string {
  return `${Math.round(g * 100)}%`
}

export function CallBoostControl({
  enabled,
  gain,
  onEnabledChange,
  onGainChange,
  position,
  onClose,
  triggerRef,
  controlsTestId,
}: CallBoostControlProps): React.ReactElement {
  const contentRef = useRef<HTMLDivElement>(null)

  // Close on outside click / Escape / scroll / resize. Mirrors AudioPlayer
  // popover behavior. Outside-click checks both the popover content AND
  // the trigger button (so clicks on the trigger don't close-then-reopen
  // through the toggle handler).
  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      const t = e.target as Node
      if (contentRef.current?.contains(t)) return
      if (triggerRef?.current?.contains(t)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    // capture: scroll doesn't bubble — listen everywhere
    window.addEventListener('scroll', onClose, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onClose, true)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose, triggerRef])

  const sliderTestId = controlsTestId ? `${controlsTestId}-slider` : undefined
  const switchTestId = controlsTestId ? `${controlsTestId}-switch` : undefined

  return createPortal(
    <div
      ref={contentRef}
      role="dialog"
      aria-label="Усиление громкости звонка"
      style={{ position: 'fixed', ...position }}
      className="z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-56"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Усиление
        </span>
        <label className="inline-flex items-center cursor-pointer select-none">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            data-testid={switchTestId}
            aria-label="Включить усиление звука звонка"
          />
          <span className="w-9 h-5 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 transition-colors relative after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:shadow-sm after:transition-transform peer-checked:after:translate-x-4" />
        </label>
      </div>

      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          Громкость
        </span>
        <span className={`text-xs font-mono tabular-nums ${
          enabled && gain > 1 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-500 dark:text-gray-400'
        }`}>
          {formatPercent(gain)}
        </span>
      </div>
      <input
        type="range"
        min={CALL_BOOST_GAIN_MIN * 100}
        max={CALL_BOOST_GAIN_MAX * 100}
        step={5}
        value={Math.round(gain * 100)}
        disabled={!enabled}
        onChange={(e) => onGainChange(Number(e.target.value) / 100)}
        data-testid={sliderTestId}
        className="w-full accent-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Уровень усиления входящего звука"
      />
      <div className="flex justify-between text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">
        <span>100</span>
        <span>200</span>
        <span>300</span>
      </div>

      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 leading-snug">
        Программное усиление входящего звука. Включи если плохо слышно собеседника.
        На 200%+ возможны лёгкие искажения на пиках.
      </p>
    </div>,
    document.body,
  )
}
